import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { categoriesAPI, providersAPI, statsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Briefcase, Home, Heart, Users, DollarSign, Scale,
  Stethoscope, Plane, ArrowRight, CheckCircle, Star, Quote, Loader2
} from 'lucide-react';

const CATEGORY_CONFIG = {
  career: { icon: Briefcase, gradient: 'from-blue-500 to-blue-600', bg: 'from-blue-50 to-blue-100', text: 'text-blue-900', name: 'Career Services', description: 'Connect with alumni who can help you find your next opportunity, review your resume, prepare for interviews, and coach you through career transitions.', features: ['Job Board', 'Career Coaching', 'Resume Review', 'Interview Prep'], actions: [{ label: 'Browse Job Postings', to: '/jobs' }, { label: 'Find Career Coaches', to: '/services?category=career' }, { label: 'Upload Resume', to: '/profile' }, { label: 'Practice Interviews', to: '/services?category=career' }] },
  real_estate: { icon: Home, gradient: 'from-emerald-500 to-emerald-600', bg: 'from-emerald-50 to-emerald-100', text: 'text-emerald-900', name: 'Real Estate', description: 'Find trusted alumni realtors who understand your needs for buying, selling, or renting property.', features: ['Find Realtors', 'Property Listings', 'Mortgage Advice', 'Roommate Matching'], actions: [{ label: 'Browse Housing', to: '/housing' }, { label: 'Find Realtors', to: '/services?category=real_estate' }, { label: 'Post a Listing', to: '/housing' }, { label: 'Find Roommates', to: '/housing' }] },
  social_dating: { icon: Heart, gradient: 'from-rose-500 to-rose-600', bg: 'from-rose-50 to-rose-100', text: 'text-rose-900', name: 'Social & Dating', description: 'Connect with fellow alumni for social events, dating, and building meaningful relationships.', features: ['Dating Profiles', 'Social Events', 'Mixers', 'Group Activities'], actions: [{ label: 'Browse Dating', to: '/dating' }, { label: 'Find Events', to: '/events' }, { label: 'Join Groups', to: '/chat' }, { label: 'Browse Profiles', to: '/dating' }] },
  networking: { icon: Users, gradient: 'from-fuchsia-500 to-fuchsia-600', bg: 'from-fuchsia-50 to-fuchsia-100', text: 'text-fuchsia-900', name: 'Networking', description: 'Expand your professional network with alumni from your institution and beyond.', features: ['Alumni Directory', 'Industry Groups', 'Mentorship', 'Networking Events'], actions: [{ label: 'Find Mentors', to: '/mentorship' }, { label: 'Browse Alumni', to: '/feed' }, { label: 'Join Events', to: '/events' }, { label: 'Start a Group', to: '/chat' }] },
  financial: { icon: DollarSign, gradient: 'from-amber-500 to-amber-600', bg: 'from-amber-50 to-amber-100', text: 'text-amber-900', name: 'Financial Services', description: 'Get trusted financial advice from alumni advisors, planners, and tax professionals.', features: ['Financial Advisors', 'Tax Services', 'Wealth Management', 'Insurance'], actions: [{ label: 'Find Advisors', to: '/services?category=financial' }, { label: 'Book Consultation', to: '/bookings' }, { label: 'Browse Providers', to: '/services?category=financial' }, { label: 'View Reviews', to: '/services?category=financial' }] },
  legal: { icon: Scale, gradient: 'from-red-500 to-red-600', bg: 'from-red-50 to-red-100', text: 'text-red-900', name: 'Legal Services', description: 'Connect with alumni attorneys for legal consultations, document review, and pro bono support.', features: ['Attorney Directory', 'Legal Consultation', 'Document Review', 'Pro Bono'], actions: [{ label: 'Find Attorneys', to: '/services?category=legal' }, { label: 'Book Consultation', to: '/bookings' }, { label: 'Browse Reviews', to: '/services?category=legal' }, { label: 'Ask a Question', to: '/chat' }] },
  healthcare: { icon: Stethoscope, gradient: 'from-teal-500 to-teal-600', bg: 'from-teal-50 to-teal-100', text: 'text-teal-900', name: 'Healthcare', description: 'Find trusted alumni healthcare providers for medical, dental, wellness, and mental health support.', features: ['Find Doctors', 'Wellness Programs', 'Mental Health', 'Dental Care'], actions: [{ label: 'Find Providers', to: '/services?category=healthcare' }, { label: 'Book Appointment', to: '/bookings' }, { label: 'Browse Reviews', to: '/services?category=healthcare' }, { label: 'Wellness Resources', to: '/blog' }] },
  travel: { icon: Plane, gradient: 'from-indigo-500 to-indigo-600', bg: 'from-indigo-50 to-indigo-100', text: 'text-indigo-900', name: 'Travel', description: 'Plan trips with alumni travel agents and find exclusive deals for group travel.', features: ['Travel Agents', 'Group Trips', 'Exclusive Deals', 'Travel Companions'], actions: [{ label: 'Find Agents', to: '/services?category=travel' }, { label: 'Group Trips', to: '/events?category=social' }, { label: 'Browse Deals', to: '/services?category=travel' }, { label: 'Travel Companions', to: '/dating' }] },
};

const CategoryPage = () => {
  const { categoryId } = useParams();
  const config = CATEGORY_CONFIG[categoryId];
  const [providerCount, setProviderCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await providersAPI.list({ category: categoryId, limit: 1 });
        // Use the response to estimate count
        setProviderCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    };
    fetchData();
  }, [categoryId]);

  if (!config) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="font-heading font-bold text-2xl text-dark-slate mb-4">Category not found</h1>
          <Link to="/dashboard" className="text-dark-slate font-semibold hover:underline">Back to Dashboard</Link>
        </div>
      </Layout>
    );
  }

  const Icon = config.icon;

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className={`bg-gradient-to-br ${config.gradient} rounded-3xl p-8 sm:p-12 text-white mb-8`}>
          <div className="flex items-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Icon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="font-heading font-extrabold text-3xl sm:text-4xl">{config.name}</h1>
              <p className="text-white/80 text-sm mt-1">Exclusive to your alumni community</p>
            </div>
          </div>
          <p className="text-white/90 text-lg leading-relaxed max-w-2xl">{config.description}</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-ambient text-center">
            <p className="font-heading font-bold text-3xl text-dark-slate">{providerCount || '—'}</p>
            <p className="text-slate-gray text-sm mt-1">Available Providers</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-ambient text-center">
            <p className="font-heading font-bold text-3xl text-dark-slate">—</p>
            <p className="text-slate-gray text-sm mt-1">Connected Alumni</p>
          </div>
        </div>

        {/* Features */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient mb-8">
          <h2 className="font-heading font-bold text-xl text-dark-slate mb-4">What You Can Do</h2>
          <div className="grid grid-cols-2 gap-3">
            {config.features.map((feature, i) => (
              <div key={i} className="flex items-center space-x-3 p-3 rounded-xl bg-slate-50">
                <CheckCircle className={`w-5 h-5 ${config.text} flex-shrink-0`} />
                <span className="font-medium text-dark-slate text-sm">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-ambient mb-8">
          <h2 className="font-heading font-bold text-xl text-dark-slate mb-4">Get Started</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {config.actions.map((action, i) => (
              <Link key={i} to={action.to}
                className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                  i === 0 ? `bg-gradient-to-r ${config.gradient} text-white` : 'bg-slate-50 text-dark-slate hover:bg-slate-100'
                }`}>
                <span className="font-medium">{action.label}</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link to={`/services?category=${categoryId}`}>
            <Button className={`bg-gradient-to-r ${config.gradient} text-white rounded-full px-8 h-12 font-semibold shadow-lg`}>
              Browse All {config.name} Providers <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </Layout>
  );
};

export default CategoryPage;
